import { type InfiniteData, useQueryClient } from '@tanstack/react-query';

import { useGetRecentlyPickedCollections } from '@pkg/hooks';

import {
    type Competition,
    type CompetitionLeaderboardResponse,
    type LeaderboardItem,
    SubmissionState,
} from '@app/client/common/structs';
import { COMPETITIONS_LEADERBOARD_CACHE } from '@app/client/resources/competitions';
import { useSubscribeToTopic, WebsocketTopicIds } from '@app/helpers';
import { type WebsocketMessageFlatPayload, WebsocketTopic } from '@app/store/types/websockets';

type SubmissionWSPayload = {
    action: 'Created' | 'Updated' | 'Removed';
    state: SubmissionState;
    competition_id: Competition['id'];
    twitter_handle: LeaderboardItem['twitter_handle'];
    updated_at: string;
    user_id: LeaderboardItem['user_id'];
    user_yat: LeaderboardItem['primary_yat'];
    wishlist_id: LeaderboardItem['competition_wishlist_id'];
    team_id?: string;
};

export function useSubmissionChangesWS(gameSlug?: string) {
    const queryClient = useQueryClient();
    const cache = [COMPETITIONS_LEADERBOARD_CACHE, gameSlug];
    const { getRecentlyPickedCollections } = useGetRecentlyPickedCollections(true);

    const onmessage = async (message) => {
        if (!gameSlug) return;
        try {
            const websocketMessage: WebsocketMessageFlatPayload<SubmissionWSPayload> = JSON.parse(message.data);
            const { topic, payload } = websocketMessage;

            if (topic === WebsocketTopic.CompetitionSubmissionChange) {
                // eslint-disable-next-line no-console
                console.debug(`WS Topic ${WebsocketTopic.CompetitionSubmissionChange} subscribed`);

                if (payload) {
                    const {
                        wishlist_id,
                        action,
                        state,
                        user_id,
                        user_yat,
                        updated_at,
                        team_id = '',
                        ...rest
                    } = payload;
                    const submitted = state === SubmissionState.Submitted;

                    queryClient.setQueryData(
                        cache,
                        (queryResponse?: InfiniteData<CompetitionLeaderboardResponse | undefined>) => {
                            if (queryResponse) {
                                const pages = queryResponse?.pages?.map((page) => {
                                    if (page?.data) {
                                        const currentData = page.data;
                                        if (action === 'Updated') {
                                            const payloadSubmission = currentData?.find(
                                                (item) => item.user_id === user_id,
                                            );
                                            if (payloadSubmission) {
                                                const indexOfPayload = currentData.indexOf(payloadSubmission);

                                                currentData[indexOfPayload] = {
                                                    ...payloadSubmission,
                                                    submitted,
                                                    submitted_at: submitted ? updated_at : undefined,
                                                };
                                                page.data = currentData;
                                            }
                                        }
                                        return page;
                                    }
                                });

                                if (action === 'Created') {
                                    const lastPageIndex = pages?.length - 1 || 0;
                                    const lastPage = pages[lastPageIndex];
                                    const newSubmission = {
                                        competition_wishlist_id: wishlist_id,
                                        wishlist_id,
                                        user_id,
                                        submitted,
                                        team_id,
                                        primary_yat: user_yat,
                                        ...rest,
                                    };

                                    const updatedPageData = [...(lastPage?.data || []), newSubmission];
                                    if (lastPage) {
                                        lastPage.data = updatedPageData;
                                    }
                                }
                                return JSON.parse(JSON.stringify({ ...queryResponse, pages }));
                            }
                            return undefined;
                        },
                    );

                    if (submitted) {
                        await getRecentlyPickedCollections();
                    }
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    };

    useSubscribeToTopic({
        id: WebsocketTopicIds.CompetitionSubmissionChange,
        topic: WebsocketTopic.CompetitionSubmissionChange,
        messageKey: 'SubmissionChanges',
        onmessage,
    });
}
